<template>
  <div class="chat-container" :class="{ 'user-container': isUser }">
    <div v-if="!isUser" class="bot-circle">R</div>

    <div class="chat-bubbles">
      <div
        :class="[
          'chat-bubble-content',
          'chat-bubble',
          isUser
            ? 'user-message'
            : canBeCopied
            ? 'copyable-message'
            : 'bot-message',
        ]"
      >
        <div v-if="canBeCopied">
          <span v-if="options.length === 0" class="copyable-content">
            <span v-html="formattedText"></span>

            <b-icon
              :title="canBeCopied ? $t('chat_box.click_to_copy') : ''"
              icon="content-copy"
              class="copy-icon"
              @click.native="copyToClipboard(copyableText)"
            />
          </span>

          <span v-if="options.length" class="copyable-content-multiple">
            <span v-html="formattedText"></span>

            <span
              v-for="(option, index) in options"
              :key="index"
              class="copyable-options"
            >
              <span>{{ option }}</span>
              <b-icon
                :title="canBeCopied ? $t('chat_box.click_to_copy') : ''"
                icon="content-copy"
                class="copy-icon"
                @click.native="copyToClipboard(option)"
              />
            </span>
          </span>
        </div>

        <span v-else v-html="formattedText"></span>
      </div>

      <div
        v-if="hasDocuments"
        :class="[
          'chat-bubble',
          isUser ? 'user-message' : 'bot-message',
          'documents-bubble',
        ]"
      >
        <strong>{{ $t('chat_box.sources') }}</strong>
        <ul class="documents-list">
          <li v-for="(doc, idx) in normalizedDocuments" :key="docKey(doc, idx)">
            <a
              v-if="doc.presigned_url"
              class="document-link"
              :href="doc.presigned_url"
              :download="doc.title || 'document'"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ doc.title || 'Untitled document' }}
            </a>
            <span v-else>{{ doc.title || 'Untitled document' }}</span>
          </li>
        </ul>
      </div>

      <div v-if="canBeCopied && !isUser" class="feedback-buttons">
        <b-icon
          icon="thumb-up-outline"
          :class="[
            'feedback-icon',
            {
              active: feedbackGiven === 'POSITIVE',
              disabled: feedbackGiven && feedbackGiven !== 'POSITIVE',
            },
          ]"
          :title="$t('chat_box.thumbs_up')"
          @click.native="submitFeedback('POSITIVE')"
        />
        <b-icon
          icon="thumb-down-outline"
          :class="[
            'feedback-icon',
            {
              active: feedbackGiven === 'NEGATIVE',
              disabled: feedbackGiven && feedbackGiven !== 'NEGATIVE',
            },
          ]"
          :title="$t('chat_box.thumbs_down')"
          @click.native="submitFeedback('NEGATIVE')"
        />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import MarkdownIt from 'markdown-it';
// eslint-disable-next-line import/no-extraneous-dependencies
import DOMPurify from 'dompurify';

const MODES = ['editor', 'inspector', 'publisher', 'report'];
// Only rewrite between these modes — they share job-level routes.
// Editor is excluded because it serves templates/productLibrary content.
const REWRITABLE_MODES = ['inspector', 'publisher', 'report'];
const MODE_PATTERN = new RegExp(`/view/(${MODES.join('|')})/`);

function getCurrentMode() {
  const path = window.location.pathname;
  return MODES.find((mode) => path.includes(`/${mode}`)) || null;
}

function rewriteLinkMode(href) {
  const currentMode = getCurrentMode();
  if (!currentMode) return href;

  const match = href.match(MODE_PATTERN);
  if (!match) return href;

  const linkMode = match[1];
  if (linkMode === currentMode) return href;

  // Never rewrite editor links or rewrite TO editor —
  // editor serves templates/productLibrary, not job views.
  if (
    linkMode === 'editor' ||
    !REWRITABLE_MODES.includes(linkMode) ||
    !REWRITABLE_MODES.includes(currentMode)
  ) {
    return href;
  }

  // Don't rewrite task-level links to report mode (not a valid route)
  if (currentMode === 'report') {
    const afterMode = href.split(`/view/${linkMode}/`)[1] || '';
    const segments = afterMode.split('/').filter(Boolean);
    if (segments.length > 1) return href;
  }

  return href.replace(`/view/${linkMode}/`, `/view/${currentMode}/`);
}

// Hook into DOMPurify to ensure all <a> tags open in new tabs
// and have their mode rewritten to match the current app mode.
// This covers both markdown-parsed links AND raw HTML links from the backend.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');

    const href = node.getAttribute('href');
    if (href) {
      node.setAttribute('href', rewriteLinkMode(href));
    }
  }
});

const md = new MarkdownIt({
  breaks: false,
  html: true,
  linkify: true,
});

export default {
  props: {
    text: {
      type: String,
      required: true,
    },
    copyText: {
      type: String,
      default: null,
    },
    documents: {
      type: Array,
      default: () => [],
      required: false,
    },
    isUser: {
      type: Boolean,
      required: true,
    },
    canBeCopied: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {
      feedbackGiven: null,
    };
  },
  computed: {
    copyableText() {
      return this.copyText == null ? this.text : this.copyText;
    },
    hasDocuments() {
      return Array.isArray(this.documents) && this.documents.length > 0;
    },
    normalizedDocuments() {
      return (this.documents || []).map((d) => ({
        title: d?.Title ?? d?.title ?? '',
        presigned_url: d?.presigned_url ?? d?.presignedUrl ?? d?.url ?? '',
      }));
    },
    formattedText() {
      const dirtyHtml = md.render(this.text);
      return DOMPurify.sanitize(dirtyHtml, { ADD_ATTR: ['target'] });
    },
  },
  methods: {
    submitFeedback(sentiment) {
      if (this.feedbackGiven) return;
      this.feedbackGiven = sentiment;
      this.$emit('feedback', sentiment);
    },
    docKey(doc, idx) {
      return (
        doc?.id ||
        doc?.key ||
        doc?.presigned_url ||
        `${doc?.title || 'doc'}-${idx}`
      );
    },
    copyToClipboard(text) {
      const raw = text == null ? '' : String(text);
      const dirtyHtml = md.render(raw);
      const html = DOMPurify.sanitize(dirtyHtml, { ADD_ATTR: ['target'] });

      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const plainText = this.htmlToPlainText(tmp);

      let copyPromise;
      if (navigator?.clipboard?.write) {
        copyPromise = navigator.clipboard
          .write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
          ])
          .catch(() => navigator.clipboard.writeText(plainText));
      } else if (navigator?.clipboard?.writeText) {
        copyPromise = navigator.clipboard.writeText(plainText);
      } else {
        copyPromise = Promise.reject(new Error('Clipboard not available'));
      }

      copyPromise
        .then(() => {
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Message copied to the clipboard!',
            type: 'is-info',
          });
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Failed to copy message to clipboard.',
            type: 'is-danger',
          });
        });
    },
    htmlToPlainText(el) {
      const blocks = new Set([
        'P',
        'DIV',
        'LI',
        'TR',
        'BLOCKQUOTE',
        'PRE',
        'HR',
      ]);
      const headings = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
      const parts = [];
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          parts.push(node.textContent);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName;
        if (tag === 'BR') {
          parts.push('\n');
          return;
        }
        const isBlock = blocks.has(tag);
        const isHeading = headings.has(tag);
        if ((isBlock || isHeading) && parts.length) {
          const last = parts[parts.length - 1];
          if (!/\n$/.test(last)) parts.push('\n');
          if (isHeading && !/\n\n$/.test(parts.join(''))) {
            parts.push('\n');
          }
        }
        if (tag === 'LI') parts.push('- ');
        if (tag === 'TD' || tag === 'TH') {
          const prev = node.previousElementSibling;
          if (prev) parts.push('\t');
        }
        Array.prototype.forEach.call(node.childNodes, walk);
        if (isBlock) parts.push('\n');
        if (isHeading) parts.push('\n');
      };
      walk(el);
      return parts
        .join('')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    },
  },
};
</script>

<style scoped>
.chat-bubble-content {
  display: flex;
  flex-direction: column;
  padding-left: 25px;
}

.copyable-options {
  display: flex;
  flex-direction: row;
}

.chat-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}

.user-container {
  flex-direction: row-reverse;
  width: 85%;
}

.bot-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background-color: #5a67d8;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border-radius: 50%;
  margin-right: 10px;
}

.user-container .bot-circle {
  margin-right: 0;
  margin-left: 10px;
}

.chat-bubbles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.chat-bubble {
  max-width: 95%;
  padding: 10px 15px;
  border-radius: 0 15px 15px 15px;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.chat-bubble:hover {
  background-color: #cfdcff;
}

.user-message {
  align-self: flex-end;
  background-color: #e6e8ff;
  border-radius: 15px 15px 0 15px;
  color: #333;
}

.bot-message {
  align-self: flex-start;
  background-color: #dfe9ff;
  color: #333;
}

.copyable-message {
  align-self: flex-start;
  background-color: #fff6e59b;
  border: 1px solid #ffe4b5;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyable-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.copyable-content-multiple {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.copy-icon {
  margin-left: auto;
  font-size: 16px;
  color: #ffa500;
}

.documents-bubble {
  cursor: default;
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
}

.documents-list {
  margin: 6px 0 0;
  padding-left: 18px;
  list-style-type: disc;
  list-style-position: outside;
}

.documents-list li {
  display: list-item;
}

.document-link {
  text-decoration: underline !important;
  color: #1f4aa8 !important;
  cursor: pointer;
}

.document-link:hover {
  color: #0d2f7a !important;
}

.chat-bubble :deep(a) {
  text-decoration: underline !important;
  color: #1f4aa8 !important;
}

.chat-bubble :deep(a:hover) {
  text-decoration: underline !important;
  color: #0d2f7a !important;
}

.chat-bubble :deep(h3) {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  margin: 18px 0 8px;
  padding: 4px 8px;
  background-color: #eef2ff;
  border-left: 3px solid #5a67d8;
  border-radius: 2px;
}

.chat-bubble :deep(h4) {
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
  margin: 12px 0 4px;
  padding-bottom: 2px;
  border-bottom: 1px solid #e2e8f0;
}

.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
  padding-left: 20px;
  margin: 4px 0;
  list-style: disc !important;
}

.chat-bubble :deep(ol) {
  list-style: decimal !important;
}

.chat-bubble :deep(ul ul),
.chat-bubble :deep(ol ul) {
  list-style: circle !important;
}

.chat-bubble :deep(li) {
  margin-bottom: 2px;
  display: list-item !important;
}

.chat-bubble :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0;
  font-size: 12px;
}

.chat-bubble :deep(th),
.chat-bubble :deep(td) {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: left;
}

.chat-bubble :deep(th) {
  background-color: #f0f0f0;
}

.chat-bubble :deep(a:hover) {
  text-decoration: underline !important;
  color: #0d2f7a !important;
}

.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
  padding-left: 20px;
  margin: 4px 0;
}

.chat-bubble :deep(li) {
  margin-bottom: 2px;
}

.chat-bubble :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0;
  font-size: 12px;
}

.chat-bubble :deep(th),
.chat-bubble :deep(td) {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: left;
}

.chat-bubble :deep(th) {
  background-color: #e8e8f0;
}

.feedback-buttons {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding-left: 4px;
}

.feedback-icon {
  font-size: 14px;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
}

.feedback-icon:hover {
  color: #5a67d8;
}

.feedback-icon.active {
  color: #5a67d8;
  cursor: default;
}

.feedback-icon.disabled {
  color: #ddd;
  cursor: default;
  pointer-events: none;
}
</style>
